import React, { useCallback, useRef, useState } from "react";
import { Modal, message } from "antd";
import MacInput from "../container/components/MacInput";
import { axiosDownload, protectedAxiosIntance } from "../API/Base";
import { useDispatch } from "react-redux";
import { setProfile } from "../../actions/Profile";

// Reusable hook + modal node for generating / downloading offline licenses
// Usage:
// const { openForLicense, offlineLicenseModal } = useOfflineLicenseGenerator(customer);
// <button onClick={() => openForLicense(license)}>Generate/Download</button>
// {offlineLicenseModal}
export const useOfflineLicenseGenerator = (customer) => {
  const dispatch = useDispatch();
  const [visible, setVisible] = useState(false);
  const [offlineLicenseData, setOfflineLicenseData] = useState({
    id: "",
    mac1: "",
    mac2: "",
    mac3: "",
    mac4: "",
    mac5: "",
    mac6: "",
  });

  // refs for auto-focus navigation
  const mac1Ref = useRef(null);
  const mac2Ref = useRef(null);
  const mac3Ref = useRef(null);
  const mac4Ref = useRef(null);
  const mac5Ref = useRef(null);
  const mac6Ref = useRef(null);

  const isValidHexa = (str) => {
    const regex = new RegExp("^[A-Fa-f0-9]{2}$");
    if (str == null) return true; // allow empty while typing
    return regex.test(str) === true;
  };

  const download = (text, license) => {
    const element = document.createElement("a");
    const fileName = license ? `${license.licenseCategoryModel.name} - ${license.licenseTypeModel.name}.txt` : "SIDLAB-offline-license.txt";
    element.style.display = "none";
    element.setAttribute("href", "data:text/plain;charset=utf-8," + encodeURIComponent(text));
    element.setAttribute("download", fileName);
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const refreshProfile = useCallback(() => {
    if (!customer?.id || !customer?.email) return;
    protectedAxiosIntance()
      .post(`customers/getCustomerData`, { id: customer.id, email: customer.email })
      .then((res) => dispatch(setProfile(res.data.data)))
      .catch((err) => console.log(err));
  }, [customer, dispatch]);

  const openForLicense = useCallback((license) => {
    if (!license) return;
    if (license.macAddresses?.length > 0) {
      //placeholder "ay7aga"
      axiosDownload()
        .get(`/licenses/generateOfflineLicense?licenseId=${license.id}&macAddress=ay7aga`)
        .then((res) => download(res.data, license));
    } else {
      setOfflineLicenseData({
        id: license?.id,
        mac1: "",
        mac2: "",
        mac3: "",
        mac4: "",
        mac5: "",
        mac6: "",
      });
      setVisible(true);
      setTimeout(() => mac1Ref.current?.focus(), 50);
    }
  }, []);

  const handleJumpToNext = (e, next, current, previous) => {
    if (e.key === "Delete" || e.key === "Backspace" || e.key === "ArrowLeft" || e.key === "ArrowRight") {
      if (e.key === "Backspace" && e.target.value.length === 0 && previous?.current) {
        previous.current.focus();
      }
    } else if (isValidHexa(e.target.value) && e.target.value.length === e.target.maxLength && e.key !== "Tab") {
      if (next?.current) {
        next.current.focus();
        if (typeof next.current.setSelectionRange === "function") {
          next.current.setSelectionRange(0, 0);
        }
      }
      if (current?.current && typeof current.current.blur === "function") {
        current.current.blur();
      }
    }
  };

  const handlePaste = async (e) => {
    const pastedText = await e?.clipboardData?.getData("text/plain");
    if (!pastedText) return;
    let splitValues;
    if (pastedText.includes("-")) {
      splitValues = pastedText.trim().split("-");
    } else if (pastedText.includes(":")) {
      splitValues = pastedText.trim().split(":");
    }
    if (!splitValues) return;
    if (splitValues.length !== 6) {
      message.error("Please Paste a valid Mac Address");
      return;
    }
    setOfflineLicenseData({
      id: offlineLicenseData.id,
      mac1: splitValues[0],
      mac2: splitValues[1],
      mac3: splitValues[2],
      mac4: splitValues[3],
      mac5: splitValues[4],
      mac6: splitValues[5],
    });
  };

  const onOk = () => {
    const { mac1, mac2, mac3, mac4, mac5, mac6, id } = offlineLicenseData;
    if ([mac1, mac2, mac3, mac4, mac5, mac6].every(isValidHexa)) {
      setVisible(false);
      const macAddress = `${mac1}:${mac2}:${mac3}:${mac4}:${mac5}:${mac6}`.toUpperCase();
      axiosDownload()
        .get(`/licenses/generateOfflineLicense?licenseId=${id}&macAddress=${macAddress}`)
        .then((res) => {
          refreshProfile();
          download(res.data);
        });
    } else {
      message.error("Please Enter A Valid MAC Address");
    }
  };

  const onCancel = () => setVisible(false);

  const offlineLicenseModal = (
    <Modal destroyOnClose={true} title="Generate Offline License" visible={visible} onOk={onOk} onCancel={onCancel}>
      <MacInput
        label={"MAC Address :"}
        handlePaste={handlePaste}
        offlineLicenseData={offlineLicenseData}
        setOfflineLicenseData={setOfflineLicenseData}
        handleJumpToNext={handleJumpToNext}
        isValidHexa={isValidHexa}
        mac1Ref={mac1Ref}
        mac2Ref={mac2Ref}
        mac3Ref={mac3Ref}
        mac4Ref={mac4Ref}
        mac5Ref={mac5Ref}
        mac6Ref={mac6Ref}
      />
    </Modal>
  );

  return { openForLicense, offlineLicenseModal };
};

export default useOfflineLicenseGenerator;
